import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { SubscriptionService } from '../../services/subscription-service';
import { LocationService, type ClosestStation, type StationSelection } from '../../services/location-service';
import { AuroraCachedApi } from '../../services/aurora-cache';
import type { Station } from '../../services/aurora-api';
import { t } from '../../translations';
import '../../components/bottom-tabs';
import { LocalStorageService } from '../../services/local-storage';
import { LocationUpdateEvent } from '../../app-index';

@customElement('app-home')
export class AppHome extends LitElement {
  @state() private latestNotification: any = null;
  @state() private hasSubscription: boolean = false;
  @state() private closestStation: ClosestStation | null = null;
  @state() private stationSelection: StationSelection = { mode: 'automatic', stationCodes: [] };
  @state() private stations: Station[] = [];
  @state() private location: { latitude: number; longitude: number } | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        position: relative;
        background: #0D1726;
      }

      main {
        min-height: 100vh;
        padding: 1rem;
        padding-bottom: 80px; /* Fixed padding: 60px tabs + 20px extra space */
        position: relative;
      }

      @supports (padding-bottom: env(safe-area-inset-bottom)) {
        main {
          padding-bottom: calc(80px + env(safe-area-inset-bottom));
        }
      }

      .welcome-card {
        background: #142138;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .welcome-card h2 {
        margin: 0 0 0.5rem 0;
        color: #ffffff;
      }

      .welcome-card p {
        margin: 0;
        color: #ffffff;
        opacity: 0.8;
      }

      .aurora-activity-card {
        background: #142138;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .aurora-activity-card h3 {
        margin: 0 0 0.5rem 0;
        color: #40f9d1;
        font-size: 1.1rem;
      }

      .aurora-section {
        margin-bottom: 1.5rem;
      }

      .aurora-section:last-child {
        margin-bottom: 0;
      }

      .aurora-section h4 {
        margin: 0 0 0.75rem 0;
        color: #40f9d1;
        font-size: 1rem;
        font-weight: 600;
      }

      .aurora-message {
        color: #ffffff;
        font-size: 1.1rem;
        margin: 0;
        line-height: 1.4;
      }

      .aurora-data {
        color: #ffffff;
        opacity: 0.85;
        margin: 0;
      }

      .aurora-data .data-item {
        margin-bottom: 0.5rem;
      }

      .aurora-data .data-item:last-child {
        margin-bottom: 0;
      }

      .data-label {
        color: #40f9d1;
        font-weight: 500;
      }

      .no-activity {
        color: #ffffff;
        opacity: 0.8;
        margin: 0;
      }

      .subscription-card {
        background: #142138;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        border-left: 4px solid #40f9d1;
      }

      .subscription-card h3 {
        margin: 0 0 0.75rem 0;
        color: #40f9d1;
        font-size: 1.1rem;
      }

      .subscription-info {
        color: #ffffff;
        line-height: 1.5;
      }

      .station-name {
        font-weight: 600;
        color: #40f9d1;
      }

      .station-distance {
        opacity: 0.85;
        font-size: 0.95rem;
      }

      .subscription-status {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-top: 0.75rem;
      }

      .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #40f9d1;
      }

      .status-text {
        font-size: 0.9rem;
        opacity: 0.8;
      }

      .manual-stations-list {
        margin-top: 0.75rem;
      }

      .manual-station-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }

      .manual-station-item:last-child {
        border-bottom: none;
      }

      .manual-station-info {
        flex: 1;
      }

      .manual-station-name {
        font-weight: 500;
        color: #40f9d1;
      }

      .station-distance {
        font-size: 0.9rem;
        opacity: 0.7;
        color: #40f9d1;
        font-weight: normal;
      }

      bottom-tabs {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
      }
    `
  ];

  async firstUpdated() {
    await Promise.all([
      this.loadLatestNotification(),
      this.loadSubscriptionStatus(),
      this.loadClosestStation(),
      this.loadStationSelection(),
      this.loadStations(),
    ]);

    // Listen for visibility changes to refresh data when page becomes visible
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
    window.addEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    window.removeEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
  }

  private handleLocationUpdate(event: LocationUpdateEvent) {
    this.location = {
      latitude: event.detail.latitude,
      longitude: event.detail.longitude
    };
  }


  private handleVisibilityChange = () => {
    if (!document.hidden) {
      this.refreshData();
    }
  }

  private handleWindowFocus = () => {
    this.refreshData();
  }

  private async refreshData() {
    await Promise.all([
      this.loadSubscriptionStatus(),
      this.loadStationSelection()
    ]);
  }

  // TODO deprecated, remove later
  private async loadLatestNotification() {
    try {
      const latest = await SubscriptionService.getLastLocationNotification();
      if (latest && latest.timestamp) {
        const nowUtc = new Date().getTime(); // Current UTC timestamp in milliseconds
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds

        // Convert notification timestamp to UTC milliseconds if it's not already
        let notificationTimeUtc: number;
        if (typeof latest.timestamp === 'string') {
          notificationTimeUtc = new Date(latest.timestamp + 'Z').getTime(); // Force UTC interpretation
        } else {
          notificationTimeUtc = latest.timestamp;
        }

        if (nowUtc - notificationTimeUtc <= thirtyMinutes) {
          // Notification is still fresh (within 30 minutes)
          this.latestNotification = latest;
        } else {
          // Notification is older than 30 minutes, remove it
          await SubscriptionService.removeLastLocationNotification();
          this.latestNotification = null;
        }
      } else {
        this.latestNotification = null;
      }
    } catch (error) {
      console.error('Error loading latest notification:', error);
      this.latestNotification = null;
    }
    this.requestUpdate();
  }

  private async loadSubscriptionStatus() {
    try {
      const subscriptionService = SubscriptionService.getInstance();
      const subscription = await subscriptionService.getSubscription();
      this.hasSubscription = !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      this.hasSubscription = false;
    }
  }

  private async loadClosestStation() {
    try {
      this.closestStation = LocalStorageService.getClosestStationData();
    } catch (error) {
      console.error('Error loading closest station:', error);
      this.closestStation = null;
    }
  }

  private async loadStationSelection() {
    try {
      this.stationSelection = LocalStorageService.getStationSelection();
    } catch (error) {
      console.error('Error loading station selection:', error);
      this.stationSelection = { mode: 'automatic', stationCodes: [] };
    }
  }

  private async loadStations() {
    try {
      this.stations = await AuroraCachedApi.getStations();
    } catch (error) {
      console.error('Error loading stations:', error);
      this.stations = [];
    }
  }

  private formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeIndicator = t('timeIndicator');

    if (timeIndicator) {
      return `${timeIndicator} ${hours}:${minutes}`;
    } else {
      return `${hours}:${minutes}`;
    }
  }

  private getAuroraChancesMessage(level: string | undefined): string {
    if (!level) return t('auroraChancesLow');

    const levelLower = level.toLowerCase();

    switch (levelLower) {
      case 'extreme':
        return t('auroraChancesVeryHigh');
      case 'very_high':
        return t('auroraChancesHigh');
      case 'high':
        return t('auroraChancesMedium');
      case 'some':
        return t('auroraChancesLow');
      default:
        return t('auroraChancesLow');
    }
  }

  private getLocalizedLevel(level: string | undefined): string {
    if (!level) return 'unknown';

    const levelLower = level.toLowerCase();
    switch (levelLower) {
      case 'some':
        return t('levelSome');
      case 'high':
        return t('levelHigh');
      case 'very_high':
        return t('levelVeryHigh');
      case 'extreme':
        return t('levelExtreme');
      default:
        return level;
    }
  }

  private formatActivityDetectionMessage(level: string | undefined, stationName: string | undefined, timestamp: number): string {
    const localizedLevel = this.getLocalizedLevel(level);
    const station = stationName || 'unknown station';
    const formattedTime = this.formatTimestamp(timestamp);
    return `${localizedLevel} ${t('activityDetectedNear')} ${station} (${formattedTime})`;
  }

  private getSelectedStations(): Station[] {
    if (this.stationSelection.mode === 'manual') {
      return this.stations.filter(station =>
        this.stationSelection.stationCodes.includes(station.code)
      );
    }
    return [];
  }

  private getStationWithDistance(station: Station): Station & { distance?: number } {
    if (!this.location) {
      return station;
    }

    const distance = LocationService.calculateDistance(
      this.location.latitude,
      this.location.longitude,
      station.coordinates.lat,
      station.coordinates.long
    );

    return { ...station, distance: Math.round(distance * 10) / 10 };
  }

  private renderSubscriptionCard() {
    if (this.stationSelection.mode === 'automatic' && this.closestStation) {
      return html`
        <div class="subscription-card">
          <h3>${t('subscriptionStatus')}</h3>
          <div class="subscription-info">
            <div>
              ${t('nearestStation')}: <span class="station-name">${this.closestStation.station.name}</span>
            </div>
            <div class="station-distance">
              ${t('stationDistance')}: ${LocationService.formatDistance(this.closestStation.distance)}
              ${this.closestStation.distance <= LocationService.MAX_NOTIFICATION_DISTANCE ? html`
                <span style="color: #40f9d1; margin-left: 0.5rem;">✓ ${t('withinRange')}</span>
              ` : html`
                <span style="color: #ff6b6b; margin-left: 0.5rem;">⚠ ${t('outsideRange')}</span>
              `}
            </div>
            <div class="subscription-status">
              <div class="status-indicator"></div>
              <span class="status-text">${t('activeSubscription')}: ${t('automaticMode')}</span>
            </div>
          </div>
        </div>
      `;
    } else if (this.stationSelection.mode === 'manual') {
      const selectedStations = this.getSelectedStations();
      return html`
        <div class="subscription-card">
          <h3>${t('subscriptionStatus')}</h3>
          <div class="subscription-info">
            ${selectedStations.length > 0 ? html`
              <div class="manual-stations-list">
                ${selectedStations.map(station => {
                  const stationWithDistance = this.getStationWithDistance(station);
                  return html`
                    <div class="manual-station-item">
                      <div class="manual-station-name">
                        ${station.name}${stationWithDistance.distance !== undefined ? html` <span class="station-distance">(${LocationService.formatDistance(stationWithDistance.distance)})</span>` : ''}
                      </div>
                    </div>
                  `;
                })}
              </div>
            ` : html`
              <div style="color: #ff6b6b; margin-top: 0.75rem;">
                ⚠ ${t('noStationsSelected')}
              </div>
            `}
            <div class="subscription-status">
              <div class="status-indicator"></div>
              <span class="status-text">${t('activeSubscription')} (${t('manualMode')})</span>
            </div>
          </div>
        </div>
      `;
    }
    return '';
  }

  render() {
    return html`
      <main>

        <div class="aurora-activity-card">
          <h3>${t('latestAuroraActivity')}</h3>

          ${this.latestNotification ? html`
            <!-- Aurora Forecast Section -->
            <div class="aurora-section">
              <h4>${t('auroraForecast')}</h4>
              <p class="aurora-message">${this.getAuroraChancesMessage(this.latestNotification.level)}</p>
            </div>

            <!-- Activity Details Section -->
            <div class="aurora-section">
              <h4>${t('activityDetails')}</h4>
              <div class="aurora-data">
                <div class="data-item">
                  ${this.formatActivityDetectionMessage(this.latestNotification.level, this.latestNotification.station_name, this.latestNotification.timestamp)}
                </div>
              </div>
            </div>
          ` : html`
            <div class="aurora-section">
              <h4>${t('noRecentActivity')}</h4>
              <p class="no-activity">${t('noActivityMessage')}</p>
            </div>
          `}
        </div>

        ${this.hasSubscription ? this.renderSubscriptionCard() : ''}
      </main>
      <bottom-tabs></bottom-tabs>
    `;
  }
}